import axios from 'axios';
import Cookies from 'js-cookie';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003/api';

// Cookie 設置
const TOKEN_COOKIE_NAME = 'auth_token';
const COOKIE_EXPIRE_DAYS = 30;
const COOKIE_PATH = '/';
const COOKIE_SECURE = process.env.NODE_ENV === 'production';
const COOKIE_SAME_SITE = 'Lax';

// 用於調試的日誌功能
const debugLog = (message, data) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[Auth Debug] ${message}`, data);
  }
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  }
  // 註釋: withCredentials 由於會導致 CORS 問題已被禁用
  // withCredentials: true
});

// 工具函數：獲取 token (首先嘗試 localStorage，然後嘗試 cookie)
const getAuthToken = () => {
  let token = null;
  
  try {
    // 嘗試從 localStorage 獲取
    token = localStorage.getItem('token');
    // debugLog('從 localStorage 獲取 token', { success: !!token });
  } catch (error) {
    debugLog('從 localStorage 獲取 token 失敗', { error: error.message });
  }
  
  // 如果從 localStorage 未獲取到，嘗試從 cookie 獲取
  if (!token) {
    try {
      token = Cookies.get(TOKEN_COOKIE_NAME);
      debugLog('從 cookie 獲取 token', { success: !!token });
    } catch (error) {
      debugLog('從 cookie 獲取 token 失敗', { error: error.message });
    }
  }
  
  return token;
};

// 工具函數：保存 token 到 localStorage 和 cookie
const saveAuthToken = (token) => {
  debugLog('開始保存 token');
  let localStorageSuccess = false;
  let cookieSuccess = false;
  
  // 嘗試保存到 localStorage
  try {
    localStorage.setItem('token', token);
    localStorageSuccess = true;
    debugLog('token 成功保存到 localStorage');
  } catch (error) {
    debugLog('保存 token 到 localStorage 失敗', { error: error.message });
  }
  
  // 保存到 cookie (作為備份)
  try {
    Cookies.set(TOKEN_COOKIE_NAME, token, {
      expires: COOKIE_EXPIRE_DAYS,
      path: COOKIE_PATH,
      secure: COOKIE_SECURE,
      sameSite: COOKIE_SAME_SITE
    });
    cookieSuccess = true;
    debugLog('token 成功保存到 cookie');
  } catch (error) {
    debugLog('保存 token 到 cookie 失敗', { error: error.message });
  }
  
  return { localStorageSuccess, cookieSuccess };
};

// 工具函數：移除 token
const removeAuthToken = () => {
  debugLog('開始移除 token');
  
  try {
    localStorage.removeItem('token');
    debugLog('已從 localStorage 移除 token');
  } catch (error) {
    debugLog('從 localStorage 移除 token 失敗', { error: error.message });
  }
  
  try {
    Cookies.remove(TOKEN_COOKIE_NAME, { path: COOKIE_PATH });
    debugLog('已從 cookie 移除 token');
  } catch (error) {
    debugLog('從 cookie 移除 token 失敗', { error: error.message });
  }
};

// Add a request interceptor to add the auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // debugLog('為請求添加認證頭', { url: config.url });
    } else {
      debugLog('未找到 token，請求未添加認證頭', { url: config.url });
    }
    
    return config;
  },
  (error) => {
    debugLog('請求攔截器錯誤', { error: error.message });
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // If we get a 401 (unauthorized), clear the token and redirect to login
      debugLog('收到 401 未授權響應', { url: error.config?.url });
      
      // 清除認證數據
      removeAuthToken();
      localStorage.removeItem('user');
      
      // 只有在非登入頁面時才重定向
      if (!window.location.pathname.includes('/login')) {
        debugLog('重定向到登入頁面');
        window.location.href = '/login';
      }
    } else if (error.response) {
      debugLog('API 響應錯誤', { 
        status: error.response.status, 
        url: error.config?.url,
        message: error.response.data?.message || '未知錯誤'
      });
    } else if (error.request) {
      debugLog('API 請求無響應', { 
        url: error.config?.url,
        message: error.message
      });
    } else {
      debugLog('API 調用出錯', { message: error.message });
    }
    
    return Promise.reject(error);
  }
);

// Auth API services
export const authService = {
  // Register a new user (已禁用)
  register: async (userData) => {
    throw new Error('註冊功能已被禁用');
  },

  // Login user
  login: async (credentials) => {
    try {
      debugLog('開始登入', { email: credentials.email });
      const response = await api.post('/auth/login', credentials);
      
      if (response.data.token) {
        debugLog('登入成功，收到 token');
        
        // 保存 token 到 localStorage 和 cookie
        const { localStorageSuccess, cookieSuccess } = saveAuthToken(response.data.token);
        
        // 如果 token 保存失敗，記錄警告但繼續執行
        if (!localStorageSuccess && !cookieSuccess) {
          console.warn('無法保存認證 token，用戶可能需要重新登入');
        }
        
        // If the response includes company data, flatten companyName for easier access
        if (response.data.company && response.data.company.name) {
          response.data.companyName = response.data.company.name;
        }
        
        // 保存用戶數據到 localStorage
        try {
          localStorage.setItem('user', JSON.stringify(response.data));
          debugLog('用戶數據保存到 localStorage 成功');
        } catch (storageError) {
          console.warn('無法保存用戶數據到 localStorage', storageError);
        }
      }
      
      return response.data;
    } catch (error) {
      debugLog('登入失敗', { 
        error: error.message,
        status: error.response?.status,
        serverMessage: error.response?.data?.message 
      });
      console.error('Error logging in:', error);
      throw error;
    }
  },

  // Logout user
  logout: () => {
    debugLog('執行登出操作');
    removeAuthToken();
    localStorage.removeItem('user');
  },

  // Get current user from local storage
  getCurrentUser: () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        // debugLog('從 localStorage 獲取當前用戶數據成功');
        return JSON.parse(userData);
      }
      debugLog('localStorage 中沒有用戶數據');
      return null;
    } catch (error) {
      debugLog('獲取當前用戶數據失敗', { error: error.message });
      console.warn('Error getting current user from localStorage:', error);
      return null;
    }
  },

  // Prevent multiple concurrent profile requests
  _profileRequest: null,
  
  // Get user profile with caching to prevent repeated calls
  getProfile: async () => {
    try {
      // If there's already a pending request, return that promise
      if (authService._profileRequest) {
        return authService._profileRequest;
      }
      
      // Create a new request and store the promise
      authService._profileRequest = api.get('/auth/profile').then(response => {
        // Clear the request reference after success
        authService._profileRequest = null;
        return response.data;
      }).catch(error => {
        // Clear the request reference on error too
        authService._profileRequest = null;
        console.error('Error fetching profile:', error);
        throw error;
      });
      
      return authService._profileRequest;
    } catch (error) {
      // Ensure the request is cleared if there's an exception
      authService._profileRequest = null;
      console.error('Error fetching profile:', error);
      throw error;
    }
  },

  // Update user profile
  updateProfile: async (profileData) => {
    try {
      console.log('Updating profile with data:', profileData);
      const response = await api.put('/auth/profile', profileData);
      
      // If we updated the username, make sure to update it in localStorage too
      if (profileData.username) {
        const currentUser = JSON.parse(localStorage.getItem('user'));
        if (currentUser) {
          currentUser.username = profileData.username;
          localStorage.setItem('user', JSON.stringify(currentUser));
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }
};

// Company API services
export const companyService = {
  // Get all companies
  getCompanies: async () => {
    try {
      const response = await api.get('/companies');
      return response.data;
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  },

  // Get company by ID
  getCompanyById: async (id) => {
    try {
      const response = await api.get(`/companies/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching company with ID ${id}:`, error);
      throw error;
    }
  },

  // Create new company
  createCompany: async (companyData) => {
    try {
      const response = await api.post('/companies', companyData);
      return response.data;
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  },

  // Update company
  updateCompany: async (id, companyData) => {
    try {
      const response = await api.put(`/companies/${id}`, companyData);
      return response.data;
    } catch (error) {
      console.error(`Error updating company with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete company
  deleteCompany: async (id) => {
    try {
      const response = await api.delete(`/companies/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting company with ID ${id}:`, error);
      throw error;
    }
  },

  // Get company users
  getCompanyUsers: async (id) => {
    try {
      const response = await api.get(`/companies/${id}/users`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching users for company with ID ${id}:`, error);
      throw error;
    }
  }
};

// User API services
export const userService = {
  // Get all users
  getUsers: async () => {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get user by ID
  getUserById: async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching user with ID ${id}:`, error);
      throw error;
    }
  },

  // Create new user (已禁用)
  createUser: async (userData) => {
    throw new Error('新增用戶功能已被禁用');
  },

  // Update user
  updateUser: async (id, userData) => {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      console.error(`Error updating user with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete user
  deleteUser: async (id) => {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting user with ID ${id}:`, error);
      throw error;
    }
  }
};

// Quotation API services
export const quotationService = {
  // Get all quotations with optional filters
  getQuotations: async (filters = {}) => {
    try {
      // Build query string from filters
      const params = new URLSearchParams();
      if (filters.customerName) params.append('customerName', filters.customerName);
      if (filters.pickupMonth) params.append('pickupMonth', filters.pickupMonth);
      if (filters.status) params.append('status', filters.status);
      // BIGLI-36：門市篩選。漏加這行會靜默失效（前端選了門市但 query string 沒帶，
      // 後端照樣回全部，畫面看起來像「篩選沒作用」卻沒有任何錯誤）。
      if (filters.store) params.append('store', filters.store);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      const queryString = params.toString();
      const url = queryString ? `/quotations?${queryString}` : '/quotations';

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching quotations:', error);
      throw error;
    }
  },

  // Get quotation statistics
  getQuotationStats: async () => {
    try {
      const response = await api.get('/quotations/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching quotation stats:', error);
      throw error;
    }
  },

  // Get quotation by ID
  getQuotationById: async (id) => {
    try {
      const response = await api.get(`/quotations/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching quotation with ID ${id}:`, error);
      throw error;
    }
  },

  // Create new quotation
  createQuotation: async (quotationData) => {
    try {
      const response = await api.post('/quotations', quotationData);
      return response.data;
    } catch (error) {
      console.error('Error creating quotation:', error);
      throw error;
    }
  },

  // RENT-55: 取得今日已存在的 quoteNumber 清單，避免 generateQuoteNumber 一進頁就鎖到碰撞號
  getTodayQuoteNumbers: async () => {
    try {
      const response = await api.get('/quotations/today-numbers');
      return response.data; // { prefix, numbers }
    } catch (error) {
      console.error('Error fetching today quote numbers:', error);
      // 失敗時回退為空清單，仍可走 server 端重試兜底
      return { prefix: '', numbers: [] };
    }
  },

  // Update quotation
  updateQuotation: async (id, quotationData) => {
    try {
      const response = await api.put(`/quotations/${id}`, quotationData);
      return response.data;
    } catch (error) {
      console.error(`Error updating quotation with ID ${id}:`, error);
      throw error;
    }
  },

  // Check if quotation has invoices
  checkQuotationHasInvoices: async (id) => {
    try {
      const response = await api.get(`/invoices/quotation/${id}`);
      return response.data && response.data.length > 0;
    } catch (error) {
      console.error(`Error checking invoices for quotation ID ${id}:`, error);
      return false; // Return false on error to be safe
    }
  },

  // Delete quotation
  deleteQuotation: async (id, deleteInvoices = false) => {
    try {
      const response = await api.delete(`/quotations/${id}?deleteInvoices=${deleteInvoices}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting quotation with ID ${id}:`, error);
      throw error;
    }
  },

  // BIGLI-118：已出貨 → 已完成（釋放庫存）；只有 accepted 可呼叫（後端擋 400）
  completeQuotation: async (id) => {
    const response = await api.patch(`/quotations/${id}/complete`);
    return response.data;
  },

  // BIGLI-118（待確認 B1）：已完成 → 退回已出貨（重新占用）
  reopenQuotation: async (id) => {
    const response = await api.patch(`/quotations/${id}/reopen`);
    return response.data;
  }
};

// Invoice API services
export const invoiceService = {
  // Get all invoices
  getInvoices: async () => {
    try {
      const response = await api.get('/invoices');
      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  },

  // Get invoice by ID
  getInvoiceById: async (id) => {
    try {
      const response = await api.get(`/invoices/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching invoice with ID ${id}:`, error);
      throw error;
    }
  },

  // Get invoices by quotation ID
  getInvoicesByQuotation: async (quotationId) => {
    try {
      const response = await api.get(`/invoices/quotation/${quotationId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching invoices for quotation ID ${quotationId}:`, error);
      throw error;
    }
  },

  // Create new invoice
  createInvoice: async (invoiceData) => {
    try {
      const response = await api.post('/invoices', invoiceData);
      return response.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  },

  // Create invoice from quotation
  createInvoiceFromQuotation: async (quotationId, invoiceData) => {
    try {
      const response = await api.post(`/invoices/from-quotation/${quotationId}`, invoiceData);
      return response.data;
    } catch (error) {
      console.error(`Error creating invoice from quotation ID ${quotationId}:`, error);
      throw error;
    }
  },

  // Update invoice
  updateInvoice: async (id, invoiceData) => {
    try {
      const response = await api.put(`/invoices/${id}`, invoiceData);
      return response.data;
    } catch (error) {
      console.error(`Error updating invoice with ID ${id}:`, error);
      throw error;
    }
  },

  // Update payment status
  updatePaymentStatus: async (id, paymentData) => {
    try {
      const response = await api.put(`/invoices/${id}/payment`, paymentData);
      return response.data;
    } catch (error) {
      console.error(`Error updating payment status for invoice ID ${id}:`, error);
      throw error;
    }
  },
  
  // Delete payment record
  deletePaymentRecord: async (invoiceId, recordId) => {
    try {
      const response = await api.delete(`/invoices/${invoiceId}/payment/${recordId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting payment record ${recordId} for invoice ID ${invoiceId}:`, error);
      throw error;
    }
  },

  // Edit payment record
  editPaymentRecord: async (invoiceId, recordId, recordData) => {
    try {
      const response = await api.put(`/invoices/${invoiceId}/payment/${recordId}`, recordData);
      return response.data;
    } catch (error) {
      console.error(`Error editing payment record ${recordId} for invoice ID ${invoiceId}:`, error);
      throw error;
    }
  },

  // Delete invoice
  deleteInvoice: async (id) => {
    try {
      const response = await api.delete(`/invoices/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting invoice with ID ${id}:`, error);
      throw error;
    }
  }
};

// AI 配置服務
export const aiConfigService = {
  // 獲取 AI 配置
  getAIConfig: async (companyId) => {
    try {
      const response = await api.get(`/company-settings/${companyId}/ai-config`);
      return response.data;
    } catch (error) {
      console.error('Error fetching AI config:', error);
      throw error;
    }
  },

  // 更新 AI 配置
  updateAIConfig: async (companyId, config) => {
    try {
      const response = await api.put(`/company-settings/${companyId}/ai-config`, config);
      return response.data;
    } catch (error) {
      console.error('Error updating AI config:', error);
      throw error;
    }
  },

  // 測試 AI 連接
  testAIConnection: async (companyId, provider, apiKey, model) => {
    try {
      const response = await api.post(`/company-settings/${companyId}/test-ai-connection`, {
        provider,
        apiKey,
        model
      });
      return response.data;
    } catch (error) {
      console.error('Error testing AI connection:', error);
      throw error;
    }
  },

  // 使用 AI 生成報價單
  generateQuotationWithAI: async (userInput) => {
    try {
      const response = await api.post('/quotations/generate-with-ai', { userInput });
      return response.data;
    } catch (error) {
      console.error('Error generating quotation with AI:', error);
      throw error;
    }
  }
};

export const rentalNotesTemplateService = {
  getTemplate: async () => {
    const response = await api.get('/company-settings/rental-notes-template');
    return response.data.rentalNotesTemplate || '';
  },
  updateTemplate: async (rentalNotesTemplate) => {
    const response = await api.patch('/company-settings/rental-notes-template', { rentalNotesTemplate });
    return response.data;
  }
};

export default api;
